import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, EventEmitter, inject, Input, NgZone, Output, ViewChild } from '@angular/core';
import { GoogleMap, GoogleMapsModule } from '@angular/google-maps';
import { environment } from '@environments/environment';
import { MapPlace, MapService } from './map.service';
import { debounceTime, fromEvent, Observable, Subscription } from 'rxjs';

// NOTE(IAN): August 2025 - Please take a look at the README.md file in this folder.


// TODO(IAN): 
// 1. Logic for creating map markers from locations
// 2. Handle input from places autocomplete
//  2.1. When entering a place, put a marker up 
// 3. Handle clicking on map to generate a new place?
// 4. Notifications to parent component
// 5. Satisfactory bounds setting
@Component({
    selector: 'app-cf-map',
    templateUrl: 'cf-map.component.html',
    styleUrls: ['./cf-map.component.scss',],
    imports: [CommonModule, GoogleMapsModule]
})
export class CfMapComponent {
    mapService = inject(MapService);

    // resizeObservable$?: Observable<Event>;
    // resizeSubscription$?: Subscription;

    @ViewChild('search') public searchElementRef!: ElementRef;
    @ViewChild(GoogleMap) googleMap!: GoogleMap;

    @Input() mapWidth = 600;

    @Input() showSearchTextbox = true;
    @Input() showLocation = true;
    @Input() panToLocation?: MapLocation;
    @Input() selectedLocation?: MapLocation;

    @Output() currentLocationChanged: EventEmitter<MapLocation> = new EventEmitter();
    @Output() locationSelected: EventEmitter<MapLocation> = new EventEmitter();
    @Output() mapIsLoaded = new EventEmitter<boolean>();

    markerList: MapLocation[] = [];
    containerWidth = '600px';

    @Input()
    get locations(): MapLocation[] {
        return this.markerList.slice();
    }

    set locations(value: MapLocation[]) {
        console.log(`Getting markers`, value);

        this.markerList = value.slice() || [];

        if (value.length > 0) {
            this.isLoaded = true;
        }

        this.setBounds(true);
    }

    constructor(
        private http: HttpClient,
        private ngZone: NgZone,
    ) {

    }

    isLoaded = false;
    //locationMarkerOptions: google.maps.AdvancedMarkerOptions = { draggable: false, icon: "https://maps.google.com/mapfiles/ms/icons/blue-pushpin.png" };
    locationPosition?: google.maps.LatLng;

    ngOnInit() {
        // Look at https://github.com/angular/components/commit/72525c8d41cd939c13eaa04569b83f2e81708f4c
        this.ngZone.runOutsideAngular(async () => {
            const Places = await google.maps.importLibrary("places");
            const latlng = await google.maps.importLibrary("core");
            const PinElement = await google.maps.importLibrary("marker") as any;
            console.log(`Loaded all libraries`);
            this.isLoaded = true;
            this.ngZone.run(() => { this.initialise(); });
        }).then(() => {
        });

        // this.resizeObservable$ = fromEvent(window, 'resize');
        // this.resizeSubscription$ = this.resizeObservable$.pipe(debounceTime(500)).subscribe(event => {
        //     const width = window.innerWidth;
        //     const height = window.innerHeight;
        //     console.log(`Width: ${width}, Height: ${height}`);
        // });

        this.containerWidth = `${this.mapWidth}px`;
    }

    // ngOnDestroy() {
    //     this.resizeSubscription$?.unsubscribe();
    // }

    initialise() {
        let autocomplete: google.maps.places.Autocomplete | undefined = undefined;

        if (this.searchElementRef) {
            try {
                autocomplete = new google.maps.places.Autocomplete(this.searchElementRef.nativeElement, {
                    fields: ["place_id", "geometry", "formatted_address", "name"],
                });
                this.safeToUse = true;
                this.mapIsLoaded.emit(true);

                if (autocomplete) {
                    autocomplete.addListener('place_changed', () => {
                        let place: google.maps.places.PlaceResult = autocomplete!.getPlace();
                        if (place) {
                            //verify result
                            if (place.geometry === undefined || place.geometry.location === undefined) {
                                return;
                            }
                            const id = place.place_id ?? '';
                            this.locationPosition = new google.maps.LatLng(place.geometry.location!.lat(), place.geometry.location!.lng());

                            const m = new MapLocation(id, place.geometry.location!.lat(), place.geometry.location!.lng(), place.name ?? 'NA', place.formatted_address);
                            this.locationSelected.emit(m);
                            this.notifyLocationSelected(m);
                            this.setBounds();

                            this.mapCenter = { lat: m.lat, lng: m.lng };
                            this.zoom = 14;
                        }
                    });
                }

            } catch (e: unknown) {
                if (e instanceof Error) {
                    console.error(e.message);
                }
            }
        } else {
            this.safeToUse = true;
            console.error(`No search element`);
        }

        this.setBounds();
    }

    onMapMarkerClicked(marker: MapLocation) {
        console.log(`Mouse Click: ${JSON.stringify(marker, null, 2)}`);
        this.locationSelected.emit(marker);
    }

    mapClicked(event: any): void {
        // console.log(`mapClicked:`, event);
        let placeId = '';
        let lat = 0;
        let lng = 0;
        if (event.placeId) {
            placeId = event.placeId;
        }

        if (event.latLng) {
            lat = event.latLng.lat();
            lng = event.latLng.lng();
        }

        if (event.placeId) {
            this.mapService.getPlace(event.placeId)
                //this.http.get(`https://places.googleapis.com/v1/places/${event.placeId}?fields=id,displayName,formattedAddress,location&key=${environment.googleMaps.apiKey}`)
                .subscribe({
                    next: (r: MapPlace) => {
                        console.log(`Clicked on MAP: ${JSON.stringify(r, null, 2)}`);
                        this.addLocationOnClick(event.placeId, r.location.latitude, r.location.longitude, r.name, r.address);
                    },
                    error: (error: any) => {
                        console.error(error);
                    }
                });
        } else {
            this.addLocationOnClick('New', lat, lng, 'New Location', '');
        }
    }

    addLocationOnClick(placeId: string, lat: number, lng: number, name: string, address: string) {
        const m = new MapLocation(placeId, lat, lng, name, address)
        // this.onMapMarkerClicked(m);
        // this.notifyLocationSelected(m);
        this.locationSelected.emit(m);
        this.notifyLocationSelected(m);
    }

    notifyLocationSelected(m: MapLocation) {
        console.log(`Marker is: ${JSON.stringify(m, null, 2)}`);
        this.currentLocationChanged.emit(m);

        this.locationPosition = new google.maps.LatLng(m.lat, m.lng);
        this.setBounds();
    }

    mapCenter: google.maps.LatLngLiteral = { lat: 51.49771570000001, lng: -0.17987509999999998 };
    zoom = 15;

    /**
     * Ask the browser for the user's current position
     */
    getCurrentLocation() {
        return new Promise((resolve, reject) => {
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        if (position) {
                            let lat = position.coords.latitude;
                            let lng = position.coords.longitude;

                            const location = {
                                lat,
                                lng,
                            };
                            resolve(location);

                            this.mapCenter = { lat: lat, lng: lng };
                            this.setBounds();
                        }
                    },
                    (error) => console.log(error)
                );
            } else {
                reject('Geolocation is not supported by this browser.');
            }
        });
    }

    addLocation(id: string, lat: number, lng: number, title?: string, address?: string) {
        this.locations.push(new MapLocation(id, lat, lng, title ?? 'N/A', address));
    }

    setBounds(all: boolean = true): void {
        if (!this.safeToUse) return;

        var smallestLat = this.markerList.sort((a, b) => a.lat > b.lat ? -1 : 1)[0] ?? 0;
        var largestLat = this.markerList.sort((a, b) => a.lat < b.lat ? -1 : 1)[0] ?? 0;

        var smallestLng = this.markerList.sort((a, b) => a.lng > b.lng ? -1 : 1)[0] ?? 0;
        var largestLng = this.markerList.sort((a, b) => a.lat < b.lat ? -1 : 1)[0] ?? 0;

        var b = new google.maps.LatLngBounds(smallestLat, largestLng);

        var bounds = all ? b : new google.maps.LatLngBounds();

        if (this.locationPosition) {
            bounds.extend(this.locationPosition);
        }

        if (all) {
            this.googleMap!.fitBounds(bounds);
            this.googleMap!.center = bounds.getCenter();
        }
    }

    options: google.maps.MapOptions = {
        cameraControl: false,
        streetViewControl: false,
        tilt: 0,
        tiltInteractionEnabled: false,
        mapId: 'DEMO_MAP_ID',
        mapTypeControl: false,
        maxZoom: 20,
    };
    safeToUse = false;
}

export class MapLocation {
    id: string;
    lat: number;
    lng: number;
    title: string;
    address?: string;
    selected = false;

    constructor(id: string, lat: number, lng: number, title: string, address: string | undefined = undefined) {
        this.id = id;
        this.lat = lat;
        this.lng = lng;
        this.title = title;
        this.address = address;
    }
}
